import { useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Wallet, Smartphone } from "lucide-react";
import { detectPlatform } from "@/lib/session";

const badgeCls = (disabled) =>
  `inline-flex items-center gap-2.5 rounded-xl bg-[#0A0A0A] text-white h-11 pl-3 pr-4 ring-1 ring-inset ring-white/10 transition-all ${
    disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-neutral-800 active:scale-[0.98]"
  }`;

function Badge({ name, top, href, disabled, testid }) {
  const inner = (
    <>
      <Wallet className="h-[18px] w-[18px] shrink-0" strokeWidth={1.6} />
      <span className="flex flex-col items-start leading-none text-left">
        <span className="text-[9.5px] text-white/70">{disabled ? "Coming soon" : top}</span>
        <span className="mt-0.5 text-[13.5px] font-medium tracking-tight">{name}</span>
      </span>
    </>
  );
  if (href && !disabled) {
    return (
      <a href={href} data-testid={testid} className={badgeCls(false)}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" disabled aria-disabled data-testid={testid} className={badgeCls(true)}>
      {inner}
    </button>
  );
}

// Add-to-Wallet, per device. Google's save link only works on a phone, so:
//   desktop -> QR code of the link (scan with your phone), never a dead-end link
//   Android -> open the Google Wallet link
//   iPhone  -> Apple Wallet "coming soon" (+ note that Google/Android exists)
// No link at all -> buttons read "Coming soon".
export default function WalletAdd({ googleUrl = null, appleUrl = null, testid = "wallet-add", className = "", platform: forced }) {
  const platform = useMemo(() => forced || detectPlatform(), [forced]);

  if (!googleUrl && !appleUrl) {
    return (
      <div data-testid={testid} data-platform={platform} className={className}>
        <div className="flex flex-wrap items-center gap-2.5">
          <Badge name="Apple Wallet" disabled testid={`${testid}-apple`} />
          <Badge name="Google Wallet" disabled testid={`${testid}-google`} />
        </div>
        <div data-testid={`${testid}-coming-soon`} className="mt-2 text-[11.5px] text-neutral-500">
          Wallet passes are rolling out. Your FitKey is ready and will be addable here soon.
        </div>
      </div>
    );
  }

  if (platform === "android") {
    return (
      <div data-testid={testid} data-platform="android" className={className}>
        <Badge name="Google Wallet" top="Add to" href={googleUrl} testid={`${testid}-google`} />
        <div className="mt-2 text-[11.5px] text-neutral-500">Opens Google Wallet on this phone.</div>
      </div>
    );
  }

  if (platform === "ios") {
    return (
      <div data-testid={testid} data-platform="ios" className={className}>
        <Badge name="Apple Wallet" top="Add to" href={appleUrl} disabled={!appleUrl} testid={`${testid}-apple`} />
        {!appleUrl && (
          <div data-testid={`${testid}-ios-note`} className="mt-2 max-w-sm text-[11.5px] text-neutral-500 leading-relaxed">
            Apple Wallet is coming soon. The Google Wallet version is already available on Android phones.
          </div>
        )}
      </div>
    );
  }

  // Desktop: QR code of the save link.
  return (
    <div data-testid={testid} data-platform="desktop" className={className}>
      {googleUrl ? (
        <div className="inline-flex items-center gap-4 rounded-2xl border border-black/10 bg-white p-3 pr-5">
          <div className="rounded-xl bg-white p-1.5 ring-1 ring-black/5">
            <QRCodeSVG
              data-testid={`${testid}-qr`}
              value={googleUrl}
              size={132}
              level="L"
              marginSize={0}
              fgColor="#0A0A0A"
              bgColor="#FFFFFF"
            />
          </div>
          <div className="max-w-[220px]">
            <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.2em] text-neutral-500">
              <Smartphone className="h-3.5 w-3.5" /> Add to Wallet
            </div>
            <div data-testid={`${testid}-qr-label`} className="mt-1.5 text-[14px] font-medium leading-snug">
              Scan with your phone to add your FitKey
            </div>
            <div className="mt-1.5 text-[11.5px] text-neutral-500 leading-relaxed">
              Opens Google Wallet on Android. Apple Wallet is coming soon.
            </div>
          </div>
        </div>
      ) : (
        <Badge name="Google Wallet" disabled testid={`${testid}-google`} />
      )}
    </div>
  );
}
